import { Component, OnInit } from '@angular/core';
import { DisplayOptions, ISyncAsOfDateInf } from '../../../shared/interfaces/display-options';
import { IKPIDataParams, KPI, KPISparklineData } from '../../../shared/interfaces/kpi-data';
import { SortDescriptor, orderBy, filterBy, State } from '@progress/kendo-data-query';
import { GridDataResult } from '@progress/kendo-angular-grid';
import {
    NoDataActionType,
    NoDataTypes,
    NoDataSourceType,
} from '../../../shared/interfaces/no-data';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { Router } from '@angular/router';
import { KPIService } from '../../../shared/services/kpi.service';
import { LimeGearCharts } from '../../../shared/classes/chart-data';
import { DecimalPipe } from '@angular/common';
import { IPageHeadline } from '../../../shared/classes/page-title-parameters';
import { HierarchyService } from 'app/modules/settings/services/hierarchy.service';
import { UserHierarchyByRole } from 'app/modules/settings/interfaces/hierarchy-data';
import { FiltersService } from 'app/modules/shared/services/filters.service';
import * as Highcharts from 'highcharts';
import { IStaffPosition } from '../../../shared/interfaces/user-location';
import {
    IPaginatedDataInfo,
    ITableColumnProperties,
    ITableDataProperties,
} from 'app/modules/shared/components/common-kendo-table/common-kendo-table.component';
import {
    IChartParameters,
    SparklineChartOptions,
} from 'app/modules/shared/classes/sparkline-chart-options';
import { ITableColumns } from 'app/modules/reports/interfaces/reporting-data';

@Component({
    selector: 'app-performance-insights-kpi-lo',
    templateUrl: './performance-insights-kpi-lo.component.html',
    styleUrls: ['./performance-insights-kpi-lo.component.scss'],
})
export class PerformanceInsightsKpiLOComponent implements OnInit {
    entityList: UserHierarchyByRole[] = [];
    saveEmployeeList: UserHierarchyByRole[] = [];
    selectedEntity: UserHierarchyByRole[] = [];
    entityListSettings: object;
    displayOptions: DisplayOptions[];
    roleName: string;
    loading: boolean;
    isDataLoaded: boolean = false;

    sort: SortDescriptor[] = [{ field: 'kpiDesc', dir: 'asc' }];
    selectedKPI: KPI;

    headerStyle: { [key: string]: string } = {
        'text-align': 'left',
        'background-color': '#223540',
        color: '#FFFFFF',
        'border-color': '#223540',
    };
    gridView: GridDataResult;
    skip = 0;
    pageSize = 10;
    sortable = {
        mode: 'single',
        allowUnsort: true,
    };
    gridState: State = {
        sort: [],
        skip: 0,
        take: 10,
    };
    scrollable = true;
    type: 'numeric' | 'input' = 'input';
    pageable = {
        refresh: true,
        info: true,
        type: this.type,
        previousNext: true,
        pageSizes: [5, 10, 20],
    };

    limeGearCharts: LimeGearCharts.LimeGearChart;
    xTitle: string = 'Week';
    records: KPISparklineData[];
    periodCnt: number = 12;
    noDataType: NoDataTypes;
    pageHeadline: IPageHeadline;
    noDataForCompany: boolean;
    kpiSearchTerm: string = '';
    syncAsOfDateInf: ISyncAsOfDateInf;
    positions: IStaffPosition[];
    selectedPosition: IStaffPosition[];
    kpiProfileSettings: object;
    parameters: IKPIDataParams;
    action: NoDataActionType;
    tableDataProperties: ITableDataProperties;
    tableColumnProperties: ITableColumnProperties;
    hierarchyIdentifier: string;
    currentPeriodData: KPISparklineData[];
    fullDataset: KPISparklineData[];
    paginatedData: KPISparklineData[];
    spco: SparklineChartOptions;

    constructor(
        private _KPIService: KPIService,
        private _router: Router,
        private _reusableFunctions: ReusableFunctions,
        private decimalPipe: DecimalPipe,
        private hierarchyService: HierarchyService,
        private filtersService: FiltersService
    ) {}

    ngOnInit() {
        scroll(0, 0); // Scroll to top of the page
        this.records = [];
        this.action = NoDataActionType.pageStart;
        this.selectedKPI = {
            kpiMatrixKey: 0,
            clientKPIMatrixDescription: '',
            kpiDisplayShort: '',
        };

        this.pageHeadline = {
            module: 'performance',
            moduleIcon: 'fa-heartbeat',
            pageTitle: 'Key Performance Indicators',
            linkArray: [
                {
                    moduleLink: 'performance/insights/kpi-employee',
                    linkTitle: 'KPI LO',
                    showLink: true,
                },
            ],
        };

        this.limeGearCharts = new LimeGearCharts.LimeGearChart(
            this._router,
            this._reusableFunctions,
            this.decimalPipe
        );

        this.entityListSettings = {
            singleSelection: true,
            text: '',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: '',
            primaryKey: '',
            enableFilterSelectAll: false,
        };

        this.kpiProfileSettings = {
            singleSelection: true,
            text: 'Select KPI profile',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'position',
            primaryKey: 'positionAbbv',
            enableFilterSelectAll: false,
        };

        this.parameters = {
            fromDatePeriod: '',
            kpiMatrix: '',
            loanType: 'All',
            loanPurpose: 'All',
            channel: 'All',
            hFilterJSON: this._reusableFunctions.getHierarchyFilters(),
            roleName: '',
            ymc: '',
            identifier: '',
            staffingKey: '',
            positionAbbv: '',
        };

        this.noDataType = {
            action: this.action,
            searchTerm: this.kpiSearchTerm,
            componentTitle: this.pageHeadline.pageTitle,
            inputSource: NoDataSourceType.table,
        };

        this.displayOptions = this._reusableFunctions.getCurrentDisplayOptions(this._router.url);
        this.currentPeriodData = [];
        this.spco = new SparklineChartOptions(this._reusableFunctions);
        this.initiateTableProperties();
        this.setDisplayOptionsSelected();
        this.setDDls();
    }

    formatKPI(kpi: KPISparklineData): string {
        let kpiValueToDisplay = '';
        if (kpi && (kpi.kpiValue !== undefined || kpi.kpiValue !== null)) {
            kpiValueToDisplay = this._reusableFunctions.formatNumber(
                kpi.kpiValue,
                kpi.precision,
                kpi.kpiFormat
            );
        }
        return kpiValueToDisplay;
    }

    setDisplayOptionsSelected() {
        let asOfDateId = 'scorecardAsOf';
        let syncWithDisplayOptionId = 'dateRange_Q_Y_3m_6m_12m';
        this.syncAsOfDateInf = {
            asOfDateId: asOfDateId,
            syncWithDisplayOptionId: syncWithDisplayOptionId,
        };
        this.parameters.fromDatePeriod = this.displayOptions.find(
            (item) => item.optionKey.toLowerCase() === syncWithDisplayOptionId.toLowerCase()
        ).selectedOptions;

        this.parameters.ymc = this.displayOptions.find(
            (item) => item.optionKey.toLowerCase() === asOfDateId.toLowerCase()
        ).selectedOptions;
        let groupByRoles = this.displayOptions.find((item) => item.optionKey === 'groupByRoles');
        if (groupByRoles) {
            this.roleName = groupByRoles.selectedOptions;
        }
        let text: string = 'Select ' + this.roleName;
        this.entityListSettings['text'] = text;
        if (this.roleName == 'Employee') {
            this.entityListSettings['labelKey'] = 'employeeName';
            this.entityListSettings['primaryKey'] = 'employeeIdentifier';
        } else {
            this.entityListSettings['labelKey'] = 'hierarchyName';
            this.entityListSettings['primaryKey'] = 'hierarchyIdentifier';
        }
        this.parameters.roleName = this.roleName;
        this.entityListSettings = Object.assign({}, this.entityListSettings);
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        let oldRole = this.parameters.roleName;
        this.displayOptions = event;
        let newRole = this.displayOptions.find((opt) => opt.optionKey === 'groupByRoles');
        this.action = NoDataActionType.filter;
        this.setDisplayOptionsSelected();
        this.reSetGrid();

        if (newRole && newRole.selectedOptions != oldRole) {
            this.setDDls();
        } else {
            this.getKPIData();
        }
    }

    onSelectionChange(event) {
        this.action = NoDataActionType.filter;
        this.reSetGrid();
        this.getKPIData();
    }

    reSetGrid() {
        this.gridView = {
            data: [],
            total: 0,
        };
    }

    getKPIData() {
        let identifier = 'employeeIdentifier';
        if (this.roleName !== 'Employee') {
            identifier = 'hierarchyIdentifier';
        }
        this.parameters.identifier = this.selectedEntity[0][identifier];
        this.loading = true;

        this.resetChartData();

        this._KPIService.getSparklineDataEmployee(this.parameters).subscribe(
            (data) => {
                this.fullDataset = data;
                this.currentPeriodData = data.filter((x) => x.isCurrentPeriod === true);
                this.tableDataProperties.gridData = this.currentPeriodData;
                this.tableDataProperties.hasData = true;
                this.tableDataProperties.isDataLoaded = true;
                this.tableDataProperties = Object.assign({}, this.tableDataProperties);
                this.loading = false;
            },
            (error) => {
                this.tableDataProperties.gridData = [];
                this.tableDataProperties.hasData = false;
                this.tableDataProperties.isDataLoaded = true;
                this.tableDataProperties.action = NoDataActionType.error;
                this.tableDataProperties = Object.assign({}, this.tableDataProperties);
            }
        );
    }

    resetChartData() {
        this.tableDataProperties.gridData = [];
        this.tableDataProperties.hasData = false;
        this.tableDataProperties.isDataLoaded = false;
        this.tableDataProperties = Object.assign({}, this.tableDataProperties);
    }

    initiateTableProperties() {
        this.tableDataProperties = {
            action: this.action,
            gridData: [],
            searchTerm: '',
            componentTitle: 'KPIs for Each Employee',
            initialPageSize: 10,
            initialSkip: 0,
            height: 700,
            recordSelection: [],
            kendoGridSelectById: '',
            hasData: false,
            isDataLoaded: true,
            pageable: true,
            useLGScroll: false,
        };

        let columnProperties: ITableColumns[] = [
            {
                fieldId: 'clientKPIMatrixDescription',
                fieldName: 'KPI',
                isHyperlink: false,
                fieldOrder: 0,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 100,
                filterable: false,
                filter: 'text',
            },
            {
                fieldId: 'wgtKPI',
                fieldName: 'Value',
                fieldOrder: 1,
                isSortable: true,
                headerAlignment: 'center',
                dataAlignment: 'center',
                dataType: 'dynamic',
                decimalPlaces: 3,
                width: 60,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
            {
                fieldId: 'pctOfTarget',
                fieldName: '% of Target',
                fieldOrder: 2,
                isSortable: true,
                headerAlignment: 'center',
                dataAlignment: 'center',
                dataType: 'percent',
                decimalPlaces: 0,
                width: 60,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
            {
                fieldId: 'valuesSparkline',
                fieldName: 'Per Month',
                fieldOrder: 3,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'chart',
                width: 100,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'wgtKPI',
                    inComponentChartBuilding: false,
                    tooltipProperties: {
                        labelField: 'kpiDisplayShort',
                        datatype: null,
                        precision: null,
                    },
                },
            },
            {
                fieldId: 'changesSparkline',
                fieldName: 'Changes Per Month',
                fieldOrder: 4,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'chart',
                width: 100,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'wgtKPI',
                    inComponentChartBuilding: false,
                    chartType: 'column',
                    isChanges: true,
                    tooltipProperties: {
                        labelField: 'kpiDisplayShort',
                        datatype: null,
                        precision: null,
                    },
                },
            },
        ];

        this.tableColumnProperties = {
            columnProperties: columnProperties,
        };
    }

    gridChangeEvent(event: IPaginatedDataInfo) {
        this.setColumnFormat();
        this.updateTableColumnProperties();
        this.tableColumnProperties = Object.assign({}, this.tableColumnProperties);
        this.paginatedData = event?.gridData;

        let chartParams: IChartParameters = {
            paginatedData: this.paginatedData,
            fullDataset: this.fullDataset,
            toGroupByColumnHeader: 'clientKPIMatrixDescription',
            calculateChange: true,
            columnList: this.tableColumnProperties.columnProperties,
            chartIdColumn: 'rank',
            chartType: 'area',
            color: 'rgb(124, 181, 236)',
        };

        setTimeout(() => {
            this.spco.setCharts(chartParams);
        }, 0);
    }

    updateTableColumnProperties() {
        let hierarchyFieldInf = this.tableColumnProperties.columnProperties.find(
            (colInf) => colInf.backUpFieldId === 'hierarchyField'
        );

        if (hierarchyFieldInf) {
            hierarchyFieldInf.fieldName = this.roleName;
        }
    }

    setColumnFormat() {
        let columnsTobeFormatted = ['wgtKPI'];
        let columnProperties = this.tableColumnProperties.columnProperties;

        columnsTobeFormatted.forEach((fieldId) => {
            let valueColumn = columnProperties.find((col) => col.fieldId === fieldId);
            if (valueColumn) {
                let format = valueColumn.dataType;
                let precision = this.currentPeriodData[0]?.precision ?? valueColumn.decimalPlaces;
                valueColumn.dataType = format as any;
                valueColumn.decimalPlaces = precision;
            }
        });
    }

    updateGrid() {
        let localData = this.searchResults();
        let orderedData = orderBy(localData, this.sort).slice(this.skip, this.skip + this.pageSize);
        this.gridView = {
            data: orderedData,
            total: localData.length,
        };
    }

    public onStateChange(state: State) {
        this.gridState = state;
        this.getKPIData();
    }

    getChartXLabel(dateRange: string): void {
        this.xTitle = 'Month';
    }

    applyNewFilters(event: string) {
        this.reSetGrid();
        this.parameters.hFilterJSON = event;
        this.action = NoDataActionType.filter;
        this.getKPIData();
    }

    setNodataType(action: NoDataActionType) {
        if (this.kpiSearchTerm && this.kpiSearchTerm.length > 0) {
            this.noDataType.action = NoDataActionType.search;
        } else {
            this.noDataType.action = action;
        }
        this.noDataForCompany = true;
        this.loading = false;
    }

    searchResults() {
        let searchedData: KPISparklineData[] = [];
        if (this.kpiSearchTerm && this.kpiSearchTerm.length > 0) {
            let searchTermTokens = this.kpiSearchTerm.split(' ');
            searchedData = filterBy(this.records, {
                logic: 'or',
                filters: [
                    {
                        field: 'kpiDesc',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                ],
            });
            if (searchTermTokens.length > 1) {
                searchedData = filterBy(searchedData, {
                    logic: 'or',
                    filters: [
                        {
                            field: 'kpiDesc',
                            operator: 'contains',
                            value: searchTermTokens[1],
                            ignoreCase: true,
                        },
                    ],
                });
            }
        } else {
            searchedData = this.records;
        }

        if (searchedData.length === 0) {
            this.noDataForCompany = true;
        }
        return searchedData;
    }

    searchRows(event) {
        this.skip = 0;
        this.kpiSearchTerm = event;
        this.setNodataType(NoDataActionType.search);
        this.noDataType.searchTerm = this.kpiSearchTerm;
        setTimeout(() => {
            this.updateGrid();
        }, 1000);
    }

    onSelectionChangeProfile(event) {
        this.reSetGrid();
        this.setSelectedProfile();
        this.getEntityList();
    }

    setSelectedProfile() {
        let defaultProfileAbbv = 'LO';
        if (!(this.selectedPosition && this.selectedPosition.length > 0)) {
            let defaultProfile = this.positions.find(
                (pos) => pos.positionAbbv === defaultProfileAbbv
            );
            if (defaultProfile) {
                this.selectedPosition = [defaultProfile];
            }
        }
    }

    setDDls() {
        this.loading = true;
        if (this.roleName && this.roleName === 'Employee') {
            this.filtersService.getPosition(this.parameters).subscribe((res) => {
                this.positions = res;
                if (res && res.length > 0) {
                    this.setSelectedProfile();
                    this.getEntityList();
                } else {
                    this.noDataForCompany = true;
                    this.loading = false;
                }
            });
        } else {
            this.getEntityList();
        }
    }

    getEntityList() {
        this.selectedEntity = [];
        this.entityList = [];
        this.reSetGrid();
        if (this.roleName.toLowerCase() === 'employee') {
            this.parameters.staffingKey = this.selectedPosition[0].staffingKey.toString();
            this.parameters.positionAbbv = this.selectedPosition[0].positionAbbv;

            this.hierarchyService.getEmployeeListByAsOfDateAndPosition(this.parameters).subscribe(
                (data) => {
                    this.entityList = data;
                    this.setNodataType(NoDataActionType.filter);
                },
                (error) => {
                    this.setNodataType(NoDataActionType.error);
                }
            );
        } else {
            this.parameters.staffingKey = '0';
            this.hierarchyService.getHierarchyByRole(this.parameters.roleName).subscribe(
                (data) => {
                    this.entityList = data;
                    this.setNodataType(NoDataActionType.filter);
                },
                (error) => {
                    this.setNodataType(NoDataActionType.error);
                }
            );
        }
    }
    onDeSelect(event) {
        this.selectedEntity = [event];
    }

    onDeSelectProfile(event) {
        this.selectedPosition = [event];
    }
}
